// src/fileUtils/spreadsheetUtils.js - Simplified version
/**
 * Simplified utilities for parsing CSV and Excel files
 * Focuses on reliability and performance with large files
 */

/**
 * Parse CSV content with robust handling of various formats
 * @param {string} content - CSV file content
 * @param {Object} options - Parsing options 
 * @returns {Object} - Parsed CSV data
 */
export function parseCSV(content, options = {}) {
  const { maxRows = 1000 } = options;
  
  try {
    // Normalize line endings
    const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Split into lines, removing empty lines
    const lines = normalizedContent.split('\n').filter(line => line.trim().length > 0);
    
    if (lines.length === 0) {
      return {
        headers: [],
        rows: [],
        summary: { rowCount: 0, columnCount: 0 }
      };
    }
    
    // Try to detect delimiter - common ones are comma, semicolon, tab
    let delimiter = ',';
    const firstLine = lines[0];
    const delimiters = [',', ';', '\t', '|'];
    const delimiterCounts = {};
    
    delimiters.forEach(d => {
      delimiterCounts[d] = (firstLine.match(new RegExp(d === '|' ? '\\|' : d, 'g')) || []).length;
    });
    
    // Find the most frequent delimiter
    let maxCount = 0;
    Object.entries(delimiterCounts).forEach(([delim, count]) => {
      if (count > maxCount) {
        maxCount = count;
        delimiter = delim;
      }
    });
    
    // Parse the CSV with the detected delimiter
    const parseRow = (line) => {
      const values = [];
      let inQuotes = false;
      let currentValue = '';
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"' && (i === 0 || line[i-1] !== '\\')) {
          inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      
      // Add the last value
      values.push(currentValue.trim());
      
      return values.map(value => {
        // Remove quotes if the cell is wrapped in quotes
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.substring(1, value.length - 1);
        }
        
        // Convert to number if it looks like one
        if (/^-?\d+(\.\d+)?$/.test(value)) {
          return parseFloat(value);
        }
        
        return value;
      });
    };
    
    // Parse headers
    const headers = parseRow(lines[0]);
    
    // Parse data rows (with maxRows limit)
    const rows = [];
    const rowLimit = Math.min(lines.length, maxRows + 1); // +1 because we skip the header
    
    for (let i = 1; i < rowLimit; i++) {
      const values = parseRow(lines[i]);
      const row = {};
      
      headers.forEach((header, index) => {
        row[header] = index < values.length ? values[index] : '';
      });
      
      rows.push(row);
    }
    
    // Create summary
    const summary = {
      rowCount: lines.length - 1, // Total rows (not just parsed ones)
      columnCount: headers.length,
      parsed: rows.length
    };
    
    return {
      headers,
      rows,
      summary
    };
  } catch (error) {
    console.error("Error parsing CSV:", error);
    
    // Return a minimal result
    return {
      headers: [],
      rows: [],
      summary: { rowCount: 0, columnCount: 0, error: error.message }
    };
  }
}

/**
 * Parse Excel file with more reliable and simpler approach
 * @param {ArrayBuffer} buffer - Excel file as ArrayBuffer
 * @returns {Promise<Object>} - Parsed Excel data
 */
export async function parseExcel(buffer) {
  try {
    // Use dynamic import with proper error handling
    let XLSX;
    try {
      XLSX = await import('xlsx').then(module => module.default || module);
    } catch (importErr) {
      console.error("Error importing XLSX library:", importErr);
      throw new Error("Failed to load Excel library");
    }
    
    if (!XLSX) {
      throw new Error("Excel library not available");
    }
    
    // DEBUGGING: Log buffer info
    console.log("Excel buffer type:", Object.prototype.toString.call(buffer));
    console.log("Excel buffer size:", buffer.byteLength || 'unknown');
    
    // Ensure buffer is properly formatted for XLSX
    let arrayBuffer;
    if (buffer instanceof ArrayBuffer) {
      arrayBuffer = buffer;
    } else if (buffer.buffer && buffer.buffer instanceof ArrayBuffer) {
      arrayBuffer = buffer.buffer;
    } else {
      // Last resort - try to convert
      arrayBuffer = new Uint8Array(buffer).buffer;
    }
    
    // Read with more robust options
    const workbook = XLSX.read(arrayBuffer, {
      type: 'array',
      cellDates: true,
      cellText: true,
      sheetStubs: true,
      WTF: true  // More detailed errors
    });
    
    console.log("Workbook loaded:", workbook.SheetNames);
    
    if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
      throw new Error("Invalid Excel file or no sheets found");
    }
    
    // Rest of your code...
    // Now extract the data with proper error reporting
    const result = {
      sheets: {},
      summary: {
        sheetCount: workbook.SheetNames.length,
        sheetNames: workbook.SheetNames,
        totalRows: 0
      }
    };
    
    // Process each sheet with better error handling
    workbook.SheetNames.forEach(sheetName => {
      try {
        const worksheet = workbook.Sheets[sheetName];
        
        if (!worksheet || !worksheet['!ref']) {
          console.warn(`Sheet ${sheetName} is empty or has no ref`);
          result.sheets[sheetName] = {
            headers: [],
            rows: [],
            rowCount: 0,
            columnCount: 0
          };
          return;
        }
        
        // Log the sheet reference for debugging
        console.log(`Sheet ${sheetName} ref:`, worksheet['!ref']);
        
        // Convert to array of arrays with better error handling
        let rawData;
        try {
          rawData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            blankrows: false
          });
          console.log(`Extracted ${rawData.length} rows from ${sheetName}`);
        } catch (sheetErr) {
          console.error(`Error converting sheet ${sheetName}:`, sheetErr);
          rawData = [];
        }
        
        // Continue with your existing processing...
        if (!rawData || rawData.length === 0) {
          // No data in sheet
          result.sheets[sheetName] = {
            headers: [],
            rows: [],
            rowCount: 0,
            columnCount: 0
          };
          return;
        }
        
        // Extract headers from first row
        const headers = (rawData[0] || []).map((header, index) => {
          return header?.toString() || `Column${index+1}`;
        });
        
        // Convert rows to objects with headers
        const rows = [];
        for (let i = 1; i < rawData.length; i++) {
          const row = {};
          headers.forEach((header, index) => {
            row[header] = index < rawData[i].length ? rawData[i][index] : '';
          });
          rows.push(row);
        }
        
        // Calculate total row count from worksheet ref
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const rowCount = range.e.r + 1 - 1; // +1 for 0-indexing, -1 for header
        
        // Store sheet data
        result.sheets[sheetName] = {
          headers,
          rows,
          rowCount,
          columnCount: headers.length
        };
        
        // Update total row count
        result.summary.totalRows += rowCount;
      } catch (sheetErr) {
        console.error(`Error processing sheet ${sheetName}:`, sheetErr);
      }
    });
    
    return result;
  } catch (error) {
    console.error("Error parsing Excel file:", error);
    // Return a minimal result with error
    return {
      sheets: {},
      summary: {
        sheetCount: 0,
        sheetNames: [],
        totalRows: 0,
        error: error.message
      }
    };
  }
}

/**
 * Format CSV data as HTML table
 * @param {Object} parsedCsv - Parsed CSV data
 * @param {number} maxRows - Maximum rows to include
 * @returns {string} - HTML table
 */
export function formatCSVAsTable(parsedCsv, maxRows = 100) {
  if (!parsedCsv || !parsedCsv.headers || !parsedCsv.rows) {
    return '<div class="no-data-message">No data available</div>';
  }
  
  const { headers, rows } = parsedCsv;
  const limitedRows = rows.slice(0, maxRows);
  
  let tableHtml = '<table border="1" style="border-collapse: collapse; width: 100%;">';
  
  // Add header row
  tableHtml += '<thead><tr>';
  headers.forEach(header => {
    tableHtml += `<th>${escapeHtml(header?.toString() || '')}</th>`;
  });
  tableHtml += '</tr></thead>';
  
  // Add data rows
  tableHtml += '<tbody>';
  limitedRows.forEach((row, index) => {
    const rowStyle = index % 2 === 0 ? '' : 'style="background-color: #f9fafb;"';
    tableHtml += `<tr ${rowStyle}>`;
    headers.forEach(header => {
      const value = typeof row[header] === 'undefined' || row[header] === null ? '' : row[header];
      tableHtml += `<td>${escapeHtml(value?.toString() || '')}</td>`;
    });
    tableHtml += '</tr>';
  });
  tableHtml += '</tbody>';
  tableHtml += '</table>';
  
  // Add note if rows were limited
  if (parsedCsv.summary?.rowCount > maxRows) {
    tableHtml += `<p style="font-style: italic; margin-top: 8px;">Showing ${maxRows} of ${parsedCsv.summary.rowCount} rows</p>`;
  }
  
  return tableHtml;
}

/**
 * Format Excel data as HTML
 * @param {Object} parsedExcel - Parsed Excel data
 * @param {number} maxRows - Maximum rows per sheet
 * @returns {string} - HTML content
 */
export function formatExcelAsHtml(parsedExcel, maxRows = 50) {
  if (!parsedExcel || !parsedExcel.sheets || Object.keys(parsedExcel.sheets).length === 0) {
    return '<div class="no-data-message">No data available</div>';
  }
  
  // If a single sheet was requested
  const activeSheet = parsedExcel.activeSheet || Object.keys(parsedExcel.sheets)[0];
  
  if (activeSheet && parsedExcel.sheets[activeSheet]) {
    const sheet = parsedExcel.sheets[activeSheet];
    
    if (!sheet.headers || !sheet.rows || sheet.rows.length === 0) {
      return `<div class="no-data-message">No data in sheet "${activeSheet}"</div>`;
    }
    
    let tableHtml = '<table border="1" style="border-collapse: collapse; width: 100%;">';
    
    // Add header row
    tableHtml += '<thead><tr>';
    sheet.headers.forEach(header => {
      tableHtml += `<th>${escapeHtml(header?.toString() || '')}</th>`;
    });
    tableHtml += '</tr></thead>';
    
    // Add data rows (limited)
    tableHtml += '<tbody>';
    const limitedRows = sheet.rows.slice(0, maxRows);
    
    limitedRows.forEach((row, index) => {
      const rowStyle = index % 2 === 0 ? '' : 'style="background-color: #f9fafb;"';
      tableHtml += `<tr ${rowStyle}>`;
      sheet.headers.forEach(header => {
        const value = typeof row[header] === 'undefined' || row[header] === null ? '' : row[header];
        tableHtml += `<td>${escapeHtml(value?.toString() || '')}</td>`;
      });
      tableHtml += '</tr>';
    });
    
    tableHtml += '</tbody></table>';
    
    // Add note if rows were limited
    if (sheet.rowCount > maxRows) {
      tableHtml += `<p style="font-style: italic; margin-top: 8px;">Showing ${maxRows} of ${sheet.rowCount} rows in sheet "${activeSheet}"</p>`;
    }
    
    return tableHtml;
  }
  
  // Default: show multiple sheets (not typically used in the preview)
  return '<div class="no-data-message">Please select a sheet to view</div>';
}

/**
 * Simple HTML escaping to prevent XSS in table output
 * @param {string} unsafe - Unescaped string
 * @returns {string} - Escaped string
 */
function escapeHtml(unsafe) {
  if (typeof unsafe !== 'string') {
    try {
      unsafe = String(unsafe);
    } catch (e) {
      return '';
    }
  }
  
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export default {
  parseCSV,
  parseExcel,
  formatCSVAsTable,
  formatExcelAsHtml
};